import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { ArrowLeft, Edit2, Play, RefreshCw } from "react-feather";
import { Link, useParams } from "react-router";
import { useGet, usePost } from "../../api";
import {
  IBasketTest,
  IBasketTestResultsResponse,
  IBasketTestRunResult,
} from "../../interfaces";
import { BasketFormModal } from "./basket-form-modal";
import { ResponsePreview } from "./response-preview";

const PAGE_SIZE = 10;

export const BasketTestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [resultsPage, setResultsPage] = useState(0);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedResult, setSelectedResult] =
    useState<IBasketTestRunResult | null>(null);
  const [running, setRunning] = useState(false);

  const {
    data: basket,
    loading: basketLoading,
    error: basketError,
    refetch: refetchBasket,
  } = useGet<IBasketTest>(id ? `/basket-testing/${id}` : null);

  const offset = resultsPage * PAGE_SIZE;
  const {
    data: results,
    loading: resultsLoading,
    refetch: refetchResults,
  } = useGet<IBasketTestResultsResponse>(
    id
      ? `/basket-testing/${id}/results?offset=${offset}&limit=${PAGE_SIZE}`
      : null
  );

  const runHook = usePost<IBasketTestRunResult, Record<string, never>>();

  const handleRun = async () => {
    setRunning(true);
    setSelectedResult(null);
    try {
      const result = await runHook.execute(`/basket-testing/${id}/run`, {});
      setSelectedResult(result);
    } finally {
      setRunning(false);
      refetchBasket();
      refetchResults();
    }
  };

  const totalPages = results ? Math.ceil(results.total / PAGE_SIZE) : 0;

  if (basketLoading) {
    return (
      <div className="text-center py-5">
        <Spinner />
      </div>
    );
  }

  if (basketError || !basket) {
    return (
      <Alert variant="danger">
        {basketError?.message ?? "Basket test not found"}
      </Alert>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-3">
        <Link
          to="/affiliate-products/basket-tests"
          className="btn btn-outline-secondary btn-sm"
        >
          <ArrowLeft size={14} />
        </Link>
        <h5 className="mb-0 flex-grow-1">{basket.name}</h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => {
            refetchBasket();
            refetchResults();
          }}
        >
          <RefreshCw size={14} />
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setShowEdit(true)}
        >
          <Edit2 size={14} className="me-1" />
          Edit
        </Button>
        <Button size="sm" onClick={handleRun} disabled={running}>
          {running ? (
            <Spinner size="sm" className="me-1" />
          ) : (
            <Play size={14} className="me-1" />
          )}
          Run
        </Button>
      </div>

      {/* Config summary */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col xs={6} md={2}>
              <div className="text-muted small">Domain</div>
              <div className="fw-semibold">{basket.domain}</div>
            </Col>
            <Col xs={6} md={1}>
              <div className="text-muted small">Country</div>
              <div className="fw-semibold">{basket.countryCode}</div>
            </Col>
            <Col xs={6} md={2}>
              <div className="text-muted small">Partner ID</div>
              <div className="small text-truncate" title={basket.partnerId}>
                {basket.partnerId}
              </div>
            </Col>
            <Col xs={6} md={1}>
              <div className="text-muted small">Currency</div>
              <div>{basket.basketCurrency ?? "—"}</div>
            </Col>
            <Col xs={6} md={1}>
              <div className="text-muted small">Amount</div>
              <div>{basket.basketAmount ?? "—"}</div>
            </Col>
            <Col xs={6} md={1}>
              <div className="text-muted small">Delivery</div>
              <div>{basket.deliveryAmount ?? "—"}</div>
            </Col>
            <Col xs={6} md={2}>
              <div className="text-muted small">Last run</div>
              <div>
                {basket.lastRunAt
                  ? new Date(basket.lastRunAt).toLocaleString()
                  : "Never"}
              </div>
            </Col>
            <Col xs={6} md={2}>
              <div className="text-muted small">Last status</div>
              <div>
                {basket.lastRunStatus ? (
                  <Badge
                    bg={
                      basket.lastRunStatus === "success" ? "success" : "danger"
                    }
                  >
                    {basket.lastRunStatus}
                  </Badge>
                ) : (
                  "—"
                )}
              </div>
            </Col>
          </Row>

          {basket.description && (
            <div className="mt-2 text-muted small">{basket.description}</div>
          )}

          {/* Items */}
          <div className="mt-3">
            <div className="text-muted small mb-1">
              Items ({basket.items.length})
            </div>
            <Table bordered size="sm" responsive className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Category</th>
                  <th>Product ID</th>
                  <th>Product URL</th>
                </tr>
              </thead>
              <tbody>
                {basket.items.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>{item.price ?? "—"}</td>
                    <td>{item.category ?? "—"}</td>
                    <td className="small text-muted">
                      {item.productId ?? "—"}
                    </td>
                    <td className="small text-muted">
                      {item.productUrl ? (
                        <a
                          href={item.productUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-truncate d-block"
                          style={{ maxWidth: 200 }}
                        >
                          {item.productUrl}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Latest run result panel */}
      {selectedResult && (
        <Card className="mb-4 border-primary">
          <Card.Header className="d-flex align-items-center gap-2">
            <strong>Latest run</strong>
            <Badge
              bg={selectedResult.status === "success" ? "success" : "danger"}
            >
              {selectedResult.status}
            </Badge>
            <span className="text-muted small ms-auto">
              {selectedResult.durationMs}ms
            </span>
          </Card.Header>
          <Card.Body>
            <details className="mb-2">
              <summary className="text-muted small">Request sent</summary>
              <pre
                className="bg-light p-2 rounded small mt-1"
                style={{ maxHeight: 200, overflow: "auto" }}
              >
                {JSON.stringify(selectedResult.requestBody, null, 2)}
              </pre>
            </details>
            <ResponsePreview
              responseBody={selectedResult.responseBody}
              status={selectedResult.status}
              errorMessage={selectedResult.errorMessage}
            />
          </Card.Body>
        </Card>
      )}

      {/* Run history */}
      <h6 className="mb-2">Run history</h6>
      {resultsLoading ? (
        <div className="text-center py-3">
          <Spinner size="sm" />
        </div>
      ) : (
        <>
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th>Run at</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Error</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {results?.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-3">
                    No runs yet
                  </td>
                </tr>
              )}
              {results?.items.map((r) => (
                <tr key={r._id}>
                  <td className="small">
                    {new Date(r.ranAt).toLocaleString()}
                  </td>
                  <td>
                    <Badge bg={r.status === "success" ? "success" : "danger"}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="small">{r.durationMs ?? "—"}ms</td>
                  <td className="small text-danger">{r.errorMessage ?? "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 small"
                      onClick={() =>
                        setSelectedResult({
                          requestBody: r.requestBody,
                          responseBody: r.responseBody,
                          status: r.status,
                          errorMessage: r.errorMessage ?? null,
                          durationMs: r.durationMs ?? 0,
                        })
                      }
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination size="sm">
              <Pagination.Prev
                disabled={resultsPage === 0}
                onClick={() => setResultsPage((p) => p - 1)}
              />
              {Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === resultsPage}
                  onClick={() => setResultsPage(i)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={resultsPage >= totalPages - 1}
                onClick={() => setResultsPage((p) => p + 1)}
              />
            </Pagination>
          )}
        </>
      )}

      <BasketFormModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        existing={basket}
        onSaved={() => {
          setShowEdit(false);
          refetchBasket();
        }}
      />
    </>
  );
};
